import * as THREE from 'three';
import { getFXScene } from './scenes/FXScene.js';
import { getTransition } from './transitions/Transition.js';
import './styles/main.css';

const clock = new THREE.Clock();
let renderer;
let transition;
let sceneA;
let sceneB;

// DOM Elements
const container = document.getElementById('container');
const progressEl = document.getElementById('progress-val');
const textureEl = document.getElementById('texture-val');
const sceneStateEl = document.getElementById('scene-val');

function isWebGLAvailable() {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch {
    return false;
  }
}

function init() {
  if (!isWebGLAvailable()) {
    container.innerHTML = `
      <div style="color: #ff4444; padding: 2rem; font-family: monospace;">
        <h2>WebGL Not Supported</h2>
        <p>Your browser or hardware does not support WebGL graphics rendering.</p>
      </div>
    `;
    return;
  }

  // Configure renderer
  renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  container.appendChild(renderer.domElement);

  // Scene A: Wireframe green icosahedrons
  const materialA = new THREE.MeshBasicMaterial({
    color: 0x00ff66,
    wireframe: true,
  });

  // Scene B: Solid flat-shaded icosahedrons with dynamic color variation
  const materialB = new THREE.MeshStandardMaterial({
    color: 0xff9900,
    flatShading: true,
    roughness: 0.4,
    metalness: 0.1,
  });

  sceneA = getFXScene({
    renderer,
    material: materialA,
    clearColor: 0x050508,
    needsAnimatedColor: false,
  });

  sceneB = getFXScene({
    renderer,
    material: materialB,
    clearColor: 0x080505,
    needsAnimatedColor: true,
  });

  transition = getTransition({ renderer, sceneA, sceneB });

  // Event Listeners
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('keydown', onKeyDown);

  animate();
}

function onWindowResize() {
  const width = window.innerWidth;
  const height = window.innerHeight;

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);

  if (transition) {
    transition.onResize(width, height);
  }
}

function onKeyDown(e) {
  if (e.code === 'Space') {
    e.preventDefault();
    if (transition) {
      transition.params.animate = !transition.params.animate;
    }
  } else if (e.code === 'KeyT') {
    e.preventDefault();
    if (transition) {
      transition.nextTexture();
    }
  } else if (e.code === 'KeyF') {
    e.preventDefault();
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }
}

function updateHUD() {
  if (!transition) return;

  const ratio = transition.params.transition;
  if (progressEl) {
    progressEl.textContent = `${Math.round(ratio * 100)}%`;
  }

  if (textureEl) {
    textureEl.textContent = `Pattern #${transition.params.texture + 1}`;
  }

  if (sceneStateEl) {
    if (ratio === 0) {
      sceneStateEl.textContent = 'Scene B (Solid Color)';
    } else if (ratio === 1) {
      sceneStateEl.textContent = 'Scene A (Wireframe)';
    } else {
      sceneStateEl.textContent = 'Blending (Transition)';
    }
  }
}

function animate() {
  requestAnimationFrame(animate);

  // Clamp delta to avoid massive jumps if the browser tab goes idle
  const delta = Math.min(clock.getDelta(), 0.1);

  if (transition) {
    transition.render(delta);
    updateHUD();
  }
}

init();
